import React, { useState, useMemo } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  ListItemText,
  TextField,
  Tooltip,
  InputAdornment,
  Popper,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { styled } from "@mui/material/styles";
import {
  MultiSelectAutocompleteProps,
  OptionType,
} from "../lead-assignment-props";
import SearchIcon from "../../../../assets/icons/searchIcon.svg";

const CustomPopper = styled(Popper)({
  zIndex: "5 !important",
  "& .MuiAutocomplete-listbox": {
    maxHeight: "250px",
    overflowY: "auto",
    padding: 0,
    margin: 0,
  },
  "& .MuiPaper-root": {
    padding: 0,
    overflow: "hidden",
  },
});

const MultiSelectAutocompleteWithProfile: React.FC<
  MultiSelectAutocompleteProps
> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select",
  noResultsTxt,
  disabled = false,
}) => {
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const [inputValue, setInputValue] = useState("");

  const selectedOptions: OptionType[] = useMemo(() => {
    return options?.filter((opt) =>
      selectedValues?.some((val: any) =>
        typeof val === "object" && val !== null
          ? val.id === opt.value
          : val === opt.value
      )
    );
  }, [selectedValues, options]);

  const handleChange = (_event: any, selected: OptionType[]) => {
    const transformed = selected?.map((opt) => ({
      id: opt.value,
      resource: opt.resource,
    }));
    onChange(transformed);
  };

  const filterOptions = React.useCallback(
    (opts: OptionType[]) => {
      const search = inputValue.trim().toLowerCase();
      if (!search && selectedValues?.length === 0) {
        return opts.slice(0, 100);
      }
      return opts
        .filter((opt) => opt.label.toLowerCase().includes(search))
        .slice(0, 100);
    },
    [inputValue, selectedValues]
  );

  const selectedLabels = selectedOptions.map((opt) => opt.label).join(", ");

  return (
    <Box
      className="autocomplete-multi-select-input-component"
      sx={{ width: "100%", maxWidth: "100%" }}
    >
      <Autocomplete
        disablePortal
        multiple
        options={options}
        disableCloseOnSelect
        popupIcon={<KeyboardArrowDownIcon />}
        value={selectedOptions}
        filterOptions={filterOptions}
        onChange={handleChange}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(opt, val) => opt.value === val.value}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === "clear" || reason === "input") {
            setInputValue(newInputValue);
          }
        }}
        PopperComponent={CustomPopper}
        renderTags={() => null}
        noOptionsText={noResultsTxt || "No options found"}
        renderOption={(props, option, { selected }) => (
          <li {...props} style={{ padding: 0 }}>
            <Box
              sx={{
                width: "100%",
                borderRadius: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                height: "45px",
                px: 2,
                py: 1,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#8879501a",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <ListItemText
                  primary={option.label}
                  secondary={option.email}
                  primaryTypographyProps={{
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    fontSize: "12px",
                    color: "#888",
                  }}
                />
              </Box>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={selected}
                style={{ marginLeft: 8 }}
              />
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selectedOptions.length === 0 ? placeholder : ""}
            sx={{
              width: "100%",
              "& input::placeholder": {
                color: "#9e9e9e",
                opacity: 1,
              },
              "& .MuiAutocomplete-inputRoot": {
                overflow: "hidden",
                maxHeight: "45px",
                },
                "& .MuiOutlinedInput-root": {
                    paddingRight: "5px !important", // override input padding
                    "& .MuiAutocomplete-endAdornment": {
                        right: "23px !important", // ✅ properly scoped adornment positioning
                    },
                },
            }}
            InputProps={{
              ...params.InputProps,
              style: {
                display: "flex",
                alignItems: "center",
                maxWidth: "100%",
              },
              startAdornment:
                selectedOptions.length > 0 ? (
                  <InputAdornment
                    position="start"
                    sx={{ maxWidth: "calc(100% - 30px)" }}
                  >
                    <Tooltip title={selectedLabels} placement="top">
                      <Box
                        sx={{
                          display: "inline-block",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "14px",
                          color: "#000",
                          maxWidth: "100%",
                        }}
                      >
                        {selectedLabels.length > 25
                          ? selectedLabels.slice(0, 25) + "..."
                          : selectedLabels}
                      </Box>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                    <InputAdornment position="end" sx={{ ml: 1 }}>
                        <img src={SearchIcon} alt="search-icon" />
                  </InputAdornment>
                </>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              style: {
                maxWidth: "100%",
              },
            }}
          />
        )}
        disabled={disabled}
      />
    </Box>
  );
};

export default MultiSelectAutocompleteWithProfile;
